import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem,
    Paper, Select, Typography,
} from '@mui/material';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';
import {
    SEASON_STATUS, SEASON_STATUS_LABELS, fetchSubleaguesForSeason, updateSeason,
} from '../../../services/seasons';
import { captureSeasonUserStats } from '../../../services/history';
import { SPELFASES } from '../../../constants/game';

/**
 * Instellingen die voor het hele seizoen gelden, en dus voor alle subleagues die
 * erop spelen.
 *
 * De spelfase stond eerder per veiling in het subleaguebeheer. Hier zet je de
 * standaard; een subleaguebeheerder kan er nog van afwijken, en dat is hieronder
 * te zien zodat je weet waar dat gebeurt.
 */
function AdminInstellingen() {
    const { season, seasonId, seasonName } = useSeason();
    const notify = useNotification();

    const [status, setStatus] = useState(season?.status || SEASON_STATUS.VOORBEREIDING);
    const [spelfase, setSpelfase] = useState(season?.spelfase || '');
    const [subleagues, setSubleagues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [capturing, setCapturing] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchSubleaguesForSeason(seasonId)
            .then((list) => { if (!cancelled) setSubleagues(list); })
            .catch((err) => {
                console.error('Kon subleagues niet ophalen:', err);
                if (!cancelled) notify.error('De subleagues konden niet opgehaald worden.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [seasonId, notify]);

    const save = async (veld, waarde, melding) => {
        setSaving(veld);
        try {
            await updateSeason(seasonId, { [veld]: waarde });
            notify.success(melding);
        } catch (err) {
            console.error(`Kon ${veld} niet opslaan:`, err);
            notify.error(`De instelling kon niet opgeslagen worden: ${err.message}`);
        } finally {
            setSaving(null);
        }
    };

    const captureStats = async () => {
        setCapturing(true);
        try {
            const res = await captureSeasonUserStats(seasonId);
            notify.success(`Statistieken vastgelegd voor ${res.usersWritten} gebruiker(s) uit ${res.subleagues} subleague(s).`);
        } catch (err) {
            console.error('Kon gebruikersstatistieken niet vastleggen:', err);
            notify.error(`Vastleggen mislukt: ${err.message}`);
        } finally {
            setCapturing(false);
        }
    };

    const afwijkers = subleagues.filter((a) => a.spelfase);
    const volgers = subleagues.length - afwijkers.length;

    return (
        <Box>
            <SeasonBreadcrumb page="Instellingen" />
            <Typography variant="h4">Instellingen</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Geldt voor {seasonName} en voor alle subleagues die erop spelen.
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Status van het seizoen</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Een afgerond seizoen is alleen-lezen: stamgegevens synchroniseren en clubs
                    toevoegen kan dan niet meer, zodat een oude eindstand niet per ongeluk
                    overschreven wordt.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 240 }}>
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                            labelId="status-label"
                            value={status}
                            label="Status"
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            {Object.values(SEASON_STATUS).map((s) => (
                                <MenuItem key={s} value={s}>{SEASON_STATUS_LABELS[s]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={() => save('status', status, `Status gezet op ${SEASON_STATUS_LABELS[status]}.`)}
                        disabled={saving !== null || status === season?.status}
                    >
                        {saving === 'status' ? 'Bezig…' : 'Opslaan'}
                    </Button>
                </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Spelfase</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    De fase waarin het spel zich bevindt. Dit is de standaard voor alle subleagues;
                    een subleaguebeheerder kan er in zijn eigen beheer van afwijken.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 240 }}>
                        <InputLabel id="spelfase-label">Spelfase</InputLabel>
                        <Select
                            labelId="spelfase-label"
                            value={spelfase}
                            label="Spelfase"
                            onChange={(e) => setSpelfase(e.target.value)}
                        >
                            <MenuItem value=""><em>Niet ingesteld</em></MenuItem>
                            {SPELFASES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={() => save('spelfase', spelfase, spelfase
                            ? `Spelfase gezet op ${spelfase}.`
                            : 'Spelfase leeggemaakt.')}
                        disabled={saving !== null || spelfase === (season?.spelfase || '')}
                    >
                        {saving === 'spelfase' ? 'Bezig…' : 'Opslaan'}
                    </Button>
                </Box>

                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    {loading ? (
                        <CircularProgress size={20} />
                    ) : subleagues.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Er speelt nog geen subleague op dit seizoen.
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {volgers} van {subleagues.length} subleague(s) volgt deze instelling.
                            </Typography>
                            {afwijkers.length > 0 && (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Deze wijken zelf af en veranderen dus niet mee:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {afwijkers.map((a) => (
                                            <Chip
                                                key={a.id}
                                                size="small"
                                                variant="outlined"
                                                label={`${a.name} · ${a.spelfase}`}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>Gebruikersstatistieken</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Legt de eindstand en het aantal weekprijzen van elke deelnemer vast in zijn
                    geschiedenis, als bron voor de Ranglijst aller tijden en de Prijzenkast. Doe dit
                    als het seizoen is afgerond en de finaleronde verwerkt is. Opnieuw draaien mag —
                    het overschrijft de eerder vastgelegde cijfers van dit seizoen.
                </Typography>
                <Button
                    variant="contained"
                    onClick={captureStats}
                    disabled={capturing}
                >
                    {capturing ? 'Bezig…' : 'Gebruikersstatistieken vastleggen'}
                </Button>
            </Paper>
        </Box>
    );
}

export default AdminInstellingen;
